import {
	Backdrop,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	Divider,
	Stack,
	Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import useAuth from "../../contexts/AuthContext";
import useSubscriptionContext from "../../contexts/SubscriptionContext";
import { PaymentMethodEnum, Subscription, SubscriptionScheduleTypeEnum } from "../../types/subscription";
import { getTotalPrice } from "../../utils/prices";
import { getSubscriptionDuration } from "../../utils/subscription";
import ConfirmDialog from "../dialog";
import SubscriptionCardLoading from "./SubscriptionCardLoading";

function SubscriptionCard({
	subscription: { id: subscriptionId },
	showActions,
}: {
	subscription: Subscription;
	showActions: boolean;
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [confirmDialogOpened, setConfirmDialogOpened] = useState<boolean>(false);
	const { cancelSubscription, reactivateSubscription } = useSubscriptionContext();
	const { updatePaymentMethod, getSubscriptionById } = useSubscriptionContext();
	const { subscription, isLoading } = getSubscriptionById(subscriptionId);
	const { currentUserClaims } = useAuth();
	const includeTax = currentUserClaims?.accountType === "Private";

	const subscriptionMutation = useMutation(
		(action: "cancel" | "reactivate") =>
			action === "cancel" ? cancelSubscription(subscriptionId) : reactivateSubscription(subscriptionId),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["subscription-" + subscriptionId]);
			},
		}
	);

	const paymentMutation = useMutation(() => updatePaymentMethod(subscriptionId), {
		onSuccess: () => {
			queryClient.invalidateQueries(["subscription-" + subscriptionId]);
		},
	});

	const queryClient = useQueryClient();

	if (isLoading || !subscription) {
		return <SubscriptionCardLoading showActions={true} />;
	}

	const [duration, period] = getSubscriptionDuration(subscription.plan.duration);

	const scheduledDowngradeQuantity = subscription?.schedule?.find(
		({ type }) => type === SubscriptionScheduleTypeEnum.Downgrade
	)?.quantity;

	return (
		<Card key={subscription.id}>
			<CardContent>
				<Backdrop
					sx={{ color: "#fff", zIndex: 999 }}
					open={subscriptionMutation.isLoading || paymentMutation.isLoading}
				>
					<CircularProgress color="inherit" />
				</Backdrop>

				<Stack gap={"5px"}>
					<Box>
						<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
							<Typography sx={{ fontWeight: "medium", fontSize: 20, marginRight: "2px" }}>
								{subscription.plan.name}
							</Typography>
							<Typography sx={{ fontWeight: "medium", fontSize: 14 }}>
								#{subscription.subscriptionNumber}
							</Typography>
						</Stack>
					</Box>
					<Divider sx={{ width: "100%" }} />

					<Stack justifyContent={"flex-start"} direction={"row"} pr={1}>
						<Typography fontSize="1rem" lineHeight={1.5}>
							{subscription.quantity > 1
								? t("subscriptions.quantity", { quantity: subscription.quantity })
								: t("subscriptions.quantitySingular", { quantity: subscription.quantity })}
						</Typography>
					</Stack>
					<Stack justifyContent={"flex-start"} direction={"row"} pr={1} alignItems={"flex-end"}>
						<Typography fontSize="1rem" lineHeight={1.5}>
							{getTotalPrice(subscription.planPricing.basePrice, subscription.quantity)}{" "}
							{subscription.planPricing.currency.code} / {duration}
							{t(period).toLowerCase()}
						</Typography>

						<Typography fontSize="0.8rem">
							&nbsp;&nbsp;
							{t(includeTax ? "subscriptionPage.incVAT" : "subscriptionPage.exVAT")}
						</Typography>
					</Stack>
					<Stack justifyContent={"flex-start"} direction={"row"} pr={1}>
						<Typography fontSize="1rem" lineHeight={1.5}>
							{t(subscription.renewalStatus ? "subscriptions.renewsOn" : "subscriptions.endsOn", {
								date: dayjs(subscription.periodEndDate).format("DD.MM.YYYY"),
							})}
						</Typography>
					</Stack>
					{scheduledDowngradeQuantity && (
						<Stack justifyContent={"flex-start"} direction={"row"} pr={1}>
							<Typography color="error" fontSize="0.8rem" lineHeight={1.5}>
								{t("subscriptionPage.downgradeScheduled", {
									downgradeQuantity: scheduledDowngradeQuantity,
									downgradeDate: dayjs(subscription.periodEndDate).format("DD.MM.YYYY"),
								})}
							</Typography>
						</Stack>
					)}
				</Stack>
			</CardContent>
			{showActions && (
				<CardActions>
					<Button
						variant="contained"
						onClick={() => navigate("/account/subscription/change/" + subscription.id, { replace: true })}
						sx={{ mt: 2 }}
					>
						{t("subscriptionPage.change")}
					</Button>

					{subscription.paymentMethod == PaymentMethodEnum.Stripe && (
						<Button
							variant="contained"
							onClick={() => {
								paymentMutation.mutate();
							}}
							sx={{ mt: 2 }}
						>
							{t("subscriptionPage.updatePaymentMethod")}
						</Button>
					)}
					{subscription.renewalStatus && (
						<>
							<Button
								variant="outlined"
								onClick={() => {
									setConfirmDialogOpened(true);
								}}
								sx={{ mt: 2 }}
							>
								{t("subscriptionPage.cancel")}
							</Button>
							<ConfirmDialog
								headerText={t("subscriptionPage.cancelSubscriptionHeader")}
								bodyText={
									`${t("subscriptionPage.cancelSubscriptionDate", {
										date: new Date(subscription.periodEndDate).toLocaleDateString(),
									})} ` +
									(subscription.nodes.length
										? `${t("subscriptionPage.cancelSubscriptionWithBuoys")}`
										: "")
								}
								agreeText={t("subscriptionPage.cancelSubscription")}
								disagreeText={t("subscriptionPage.IWantToKeepMySubscription")}
								onConfirm={() => {
									setConfirmDialogOpened(false);

									subscriptionMutation.mutate("cancel");
								}}
								onDismiss={() => setConfirmDialogOpened(false)}
								open={confirmDialogOpened}
							/>
						</>
					)}
					{!subscription.renewalStatus && (
						<Button
							variant="outlined"
							onClick={() => {
								subscriptionMutation.mutate("reactivate");
							}}
							sx={{ mt: 2 }}
						>
							{t("subscriptionPage.reactivate")}
						</Button>
					)}
				</CardActions>
			)}
		</Card>
	);
}

export default SubscriptionCard;
